// @flow
import BaseModel from 'Stores/BaseModel';
import AssetModel from 'Stores/AssetModel';
import { isInvalid, validateLength, validateNumber } from 'Utils/Validators';
import { formatDate, formatPrice } from 'Utils';
import axios from 'axios';
import { withInheritQueryParam, withVisibilityQueryParam } from 'Utils/withInheritQueryParam';
import { isAfter } from 'date-fns';
import ListModel from 'Stores/ListModel';

type Price = {
  price: number,
  priceText: string,
  footnote: string,
  pricePrefix: string,
};

type Cta = {
  additional: string,
  buttonVariant: 'primary' | 'secondary' | 'tertiary',
  class: string,
  href: string,
  label: string,
  mazdaForm: boolean,
  model: string,
  target: '_self' | '_top' | '_parent' | '_blank',
  theme: 'primary' | 'secondary',
  trackingLabel: string,
};

export class ServiceOfferModel extends BaseModel {
  bodyText: string = '';
  footnote: string = '';
  header1: string = '';
  header2: string = '';
  headlineLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id: number = 0;
  image: ?AssetModel = null;
  modalType: 'OSB' | 'OSR';
  price: string = '';
  priceInfo: string = '';
  sorting: number = 0;
  tireDataSheet: ?{
    enabled: boolean,
    image: ?AssetModel,
    label: string,
    url: string,
  };

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);

    this.image = this.image ? new AssetModel(this.image) : null;
    this.copy = data.bodyText;
    this.bodyText = data.copy;
    this.headline1 = data.header1;
    this.headline2 = data.header2;
    this.headlineLevel = data.headlineLevel;
    this.tireDataSheet = data.tireDataSheet;
    this.modalType = data.modalType;
  }
}

export default class OfferModel extends BaseModel {
  id: number = 0;
  sorting: number = 0;
  parentId: ?number = null;
  hidden: boolean = true;
  custom: boolean = true;
  offerType: string = 'CAMPAIGN';
  carlineDeeplink: string = '';
  campaignName: string = '';
  carlineName: string = '';
  consumptionInfoDescription: string = '';
  image: ?AssetModel = null;
  starttime: ?Date = new Date();
  endtime: ?Date = new Date();
  cashPrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };
  leftPrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };
  rightPrice: Price = {
    price: 0,
    priceText: '',
    footnote: '',
    pricePrefix: '',
  };
  cta1: ?Cta = null;
  cta2: ?Cta = null;
  bodyText: string = '';
  headline: ?{
    forceUppercase: boolean,
    level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    value: string | React$Node,
  };

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);

    this.image = this.image ? new AssetModel(this.image) : null;
    this.headline = data.headline;
  }

  valid = () => {
    this.errors = {};

    if (this.cashPrice) {
      validateNumber(this.errors, 'cashPricePrice', this.cashPrice.price, 1, 99999);
      validateLength(this.errors, 'cashPriceFootnote', this.cashPrice.footnote, 1, 3000);
      // validate price text and check if term netto or brutto exists?
    }

    if (this.leftPrice) {
      validateNumber(this.errors, 'leftPricePrice', this.leftPrice.price, 1, 9999);
      validateLength(this.errors, 'leftPriceFootnote', this.leftPrice.footnote, 1, 3000);
      // validate price text and check if term netto or brutto exists?
    }

    if (this.rightPrice) {
      validateNumber(this.errors, 'rightPricePrice', this.rightPrice.price, 1, 9999);
      validateLength(this.errors, 'rightPriceFootnote', this.rightPrice.footnote, 1, 3000);
      // validate price text and check if term netto or brutto exists?
    }

    return !isInvalid(this.errors);
  };

  toListItem = () => {
    const subline = [];
    if (this.cashPrice) {
      subline.push(`Barpreis ${formatPrice(this.cashPrice.price)}`);
    }
    if (this.leftPrice) {
      subline.push(`Linker Preis ${formatPrice(this.leftPrice.price)}`);
    }
    if (this.rightPrice) {
      subline.push(`Rechter Preis ${formatPrice(this.rightPrice.price)}`);
    }
    if (this.isScheduled() && this.starttime) {
      subline.push(`Gültig ab: ${formatDate(this.starttime)}`);
    }
    if (!this.isScheduled() && this.endtime) {
      subline.push(`Gültig bis: ${formatDate(this.endtime)}`);
    }

    let headline = this.headline.value;

    if (this.campaignName) {
      headline += ` (${this.campaignName})`;
    }

    return {
      id: this.id,
      index: this.sorting,
      headline: headline,
      subline: subline.join(' | '),
      hidden: this.hidden,
      cta1: this.cta1,
      cta2: this.cta2,
      image: this.image,
      model: this,
    };
  };

  getFootnotes = () => {
    return [
      this.cashPrice && this.cashPrice.footnote,
      this.leftPrice && this.leftPrice.footnote,
      this.rightPrice && this.rightPrice.footnote,
    ].filter(Boolean);
  };

  hasError = (prop: string) => {
    return this.errors[prop] && this.errors[prop].length > 0;
  };

  save = (inherit?: boolean, visibility?: boolean) => {
    return axios
      .patch(
        withVisibilityQueryParam(
          withInheritQueryParam(`/api/v1/offers/${this.id}/`, inherit),
          visibility
        ),
        this.toJSON()
      )
      .then((res) => new OfferModel(res.data));
  };

  toJSON = () => {
    return {
      id: this.id,
      sorting: this.sorting,
      parentId: this.parentId,
      hidden: this.hidden,
      custom: this.custom,
      offerType: this.offerType,
      image: this.image,
      cashPrice: this.cashPrice,
      leftPrice: this.leftPrice,
      rightPrice: this.rightPrice,
      cta1: this.cta1,
      cta2: this.cta2,
      bodyText: this.bodyText,
      header: this.headline.value,
    };
  };

  isScheduled = () => {
    return isAfter(new Date(this.starttime), new Date());
  };

  static getAll = (params: Object = {}) => {
    return axios({
      method: 'get',
      url: '/api/v1/offers/',
      params,
    }).then((res: { data: Array<Object> }) => {
      const { data: offers = [] } = res;

      return new ListModel(offers, OfferModel);
    });
  };
}
